import React from "react";
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from "recharts";

const OccupancyCharts = ({ item, reportType, chartData = [] }) => {
  // Updated colors for charts
  const COLORS = ['#2464EC', '#82C0CB', '#FFBB28', '#FF8042', '#8884d8'];

  // Format date for display (no JST conversion)
  const formatDisplayDate = () => {
    if (reportType === "daily") {
      // For daily, show the selected date from item data or current date as fallback
      if (item.date) {
        const date = new Date(item.date);
        return date.toLocaleDateString('en-GB', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric'
        });
      }
      // Fallback to current date if no item date
      const now = new Date();
      return now.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric'
      });
    } else {
      // For weekly/custom, show date range
      if (item.date_range) {
        const dates = item.date_range.split(' to ');
        if (dates.length === 2) {
          const startDate = new Date(dates[0]);
          const endDate = new Date(dates[1]);
          const formattedStart = startDate.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric'
          });
          const formattedEnd = endDate.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric'
          });
          return `${formattedStart} to ${formattedEnd}`;
        }
      }
    }
    
    // Fallback to current date
    return new Date().toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    });
  };

// Prepare pie chart data based on revised logic for daily report
const preparePieChartData = () => {
  if (reportType === "daily") {
    // For daily reports, calculate average occupancy percentage from business hours (9-18 -> 10 hours)
    if (chartData && chartData.length > 0) {
      const businessHoursData = chartData.filter(d => {
        const hour = d.hour || 0;
        return hour >= 9 && hour <= 18;
      });
      
      const totalOccupied = businessHoursData.reduce((acc, dataPoint) => {
        return acc + (dataPoint.occupancy || 0);
      }, 0);
      
      const averageOccupied = businessHoursData.length ? (totalOccupied / 10) : 0; // Divide by 10 business hours
      const available = Math.max(0, 100 - averageOccupied);
      
      return [
        { name: "Occupied", value: averageOccupied },
        { name: "Available", value: available }
      ];
    } else {
      // Fallback: use item's occupancy percentage directly
      const occupiedValue = parseFloat(item.occupancy_percentage?.toString().replace('%', '') || 0);
      const availableValue = Math.max(0, 100 - occupiedValue);
      
      return [
        { name: "Occupied", value: occupiedValue },
        { name: "Available", value: availableValue }
      ];
    }
  } else {
    // For other report types, use different logic
    if (chartData && chartData.length > 0) {
      const totalOccupied = chartData.reduce((acc, dataPoint) => {
        return acc + (dataPoint.occupancy || 0);
      }, 0);
      const averageOccupied = totalOccupied / chartData.length;
      const available = Math.max(0, 100 - averageOccupied);
      
      return [
        { name: "Occupied", value: averageOccupied },
        { name: "Available", value: available }
      ];
    } else {
      // Fallback
      const occupiedValue = parseFloat(item.occupancy_percentage?.toString().replace('%', '') || 0);
      const availableValue = Math.max(0, 100 - occupiedValue);
      
      return [
        { name: "Occupied", value: occupiedValue },
        { name: "Available", value: availableValue }
      ];
    }
  }
};

  // Get chart title based on report type
  const getChartTitle = () => {
    switch (reportType) {
      case "daily":
        return "Hourly Trend (Business Hours)";
      case "weekly":
        return "Daily Trend (7 Days)";
      case "monthly":
        return "Daily Trend (Monthly)";
      case "custom":
        return "Daily Trend (Custom Range)";
      default:
        return "Occupancy Trend";
    }
  };

  // Get X-axis label
  const getXAxisLabel = () => {
    switch (reportType) {
      case "daily":
        return "Hour";
      case "weekly":
      case "monthly":
      case "custom":
        return "Date Range";
      default:
        return "Time";
    }
  };

  // Custom tooltip to show multiple fields (needed to display both occupancy percentage
  // and either peak occupancy for hourly or average occupancy for daily endpoints)
  const CustomTooltip = ({ active, payload, label, reportType }) => {
    if (!active || !payload || payload.length === 0) return null;
    const d = payload[0]?.payload || {};
    if (reportType === 'daily') {
      const peakRaw = d.peakOccupancy ?? d.peak_occupancy ?? d.occupancy ?? 0;
      const peak = typeof peakRaw === 'number' ? peakRaw : Number(peakRaw || 0);
      const pctNum = typeof d.occupancy === 'number' ? d.occupancy : Number(d.occupancy || 0);
      const pct = pctNum.toFixed(2);
      return (
        <div className="bg-white p-2 rounded shadow border text-sm">
          <div className="font-medium mb-1">{d.hour_display || label}</div>
          <div>Peak Occupancy: {peak}</div>
          <div>Occupancy Percentage: {pct}%</div>
        </div>
      );
    }
    const avgRaw = d.avg_occupancy ?? d.avgOccupancy ?? d.occupancy ?? 0;
    const avg = (typeof avgRaw === 'number' ? avgRaw : Number(avgRaw || 0)).toFixed(2);
    const pctNum = typeof d.occupancy === 'number' ? d.occupancy : Number(d.occupancy || 0);
    const pct = pctNum.toFixed(2);
    return (
      <div className="bg-white p-2 rounded shadow border text-sm">
        <div className="font-medium mb-1">{d.date_display || label}</div>
        <div>Average Occupancy: {avg}</div>
        <div>Occupancy Percentage: {pct}%</div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 border-t border-gray-200">
      <h2 className="text-lg font-semibold mb-4">
        Data for {item.area_id} - {formatDisplayDate()}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div>
          <h3 className="text-lg font-medium mb-4">Occupancy Overview</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={preparePieChartData()}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                fill="#2464EC"
                dataKey="value"
              >
                {preparePieChartData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => {
                // For pie chart, show percentage with % symbol
                return [`${Number(value).toFixed(1)}%`, name];
              }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        {/* Bar Chart */}
        <div>
          <h3 className="text-lg font-medium mb-4">{getChartTitle()}</h3>
          {chartData && chartData.length > 0 ? (
 <ResponsiveContainer width="100%" height={380}>
  <BarChart 
    data={chartData}
    margin={{ top: 10, right: 20, left: 20, bottom: 60 }} // extra space at bottom for x-axis label
  >
    <CartesianGrid strokeDasharray="3 3" vertical={false} />
    
    <XAxis 
      dataKey={reportType === "daily" ? "hour_display" : "date_display"}
      label={{ value: getXAxisLabel(), position: 'insideBottom', offset: -5 }} // adjust label position
      tick={{ fontSize: 12 }}
      angle={0}
      textAnchor="middle"
      height={40} // reserve more space for labels
    />

    <YAxis 
      domain={[0, 100]}
      ticks={[0, 20, 40, 60, 80, 100]}
      label={{ 
        value: 'Occupancy Percentage (%)', 
        angle: -90, 
        position: 'insideLeft',
        style: { textAnchor: 'middle' }
      }} 
    />

    <Tooltip content={<CustomTooltip reportType={reportType} />} />
    
    {/* Legend removed, so no "Average Occupancy" text */}
    <Bar 
      dataKey="occupancy" 
      radius={[4, 4, 0, 0]} 
      barSize={40} 
      fill="#2464EC" 
    />
  </BarChart>
</ResponsiveContainer>

          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-gray-500">
                {reportType === "daily" ? "Loading current hour data..." : "Loading trend data..."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OccupancyCharts;
